import { useMutation, useQuery } from '@apollo/client';
import { useMemo, useState } from 'react';
import { toast } from 'sonner';
import { ulid } from 'ulid';

import { FormPageLayout } from '@/components/layout/form-page-layout';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { DelayedSkeleton } from '@/components/ui/delayed-skeleton';
import { useRouteParams } from '@/contexts/RouteContext';
import { useDelayedLoading } from '@/hooks/useDelayedLoading';
import { useEditPageHeader } from '@/hooks/useEditPageHeader';
import { useGo } from '@/hooks/useResourceNavigation';
import { getMutationOptions } from '@/lib/mutationRefetch';

import { ContractForm } from './contract-form';
import {
	type ContractFormSubmitData,
	type ContractFormValues
} from './contract-input';
import {
	CONTRACT_DETAIL_QUERY,
	UPDATE_CONTRACT_MUTATION
} from './contracts.gql';
import { useCustomerOptions } from './server-data-hooks';

interface ContractDetailNode {
	contractId: string;
	contractNumber: string;
	beginDate: string;
	endDate: string;
	amount?: number | null;
	isAutoRenewed: boolean;
	simultaneousVehicleLimit?: number | null;
	reservedParkingSlots?: number | null;
	isActive: boolean;
	preferredLanguage?: string | null;
	customer?: {
		customerId: string;
		firmName?: string | null;
		firstName?: string | null;
		lastName?: string | null;
		isCompany?: boolean | null;
		customerCategory?: {
			description?: string | null;
			simultaneousVehicleLimit?: number | null;
		} | null;
	} | null;
	contractVehicles?: Array<{
		contractVehicleId: string;
		vehicleId: string;
		rfidTag?: string | null;
		isVip: boolean;
		vehicle?: {
			plateNumber?: string | null;
		} | null;
	}> | null;
}

interface ContractDetailQueryResponse {
	contract?: ContractDetailNode | null;
}

export const ContractsEdit = () => {
	const { id } = useRouteParams();
	const go = useGo();
	useEditPageHeader({ id, resourceName: 'contracts' });
	const [isSubmitting, setIsSubmitting] = useState(false);

	// GraphQL Operations
	const { data, loading, error } = useQuery<ContractDetailQueryResponse>(
		CONTRACT_DETAIL_QUERY,
		{
			variables: { contractId: id },
			fetchPolicy: 'cache-and-network',
			errorPolicy: 'all',
			skip: !id
		}
	);

	const [updateContractMutation] = useMutation(UPDATE_CONTRACT_MUTATION);

	const { shouldShowLoading } = useDelayedLoading(loading, {
		delay: 300,
		showInitialLoading: false
	});

	const { customerOptions: baseCustomerOptions } = useCustomerOptions();

	// Vehicle types are no longer needed since vehicle details are not editable in contracts

	// Get contract from GraphQL data only
	const contractNode = data?.contract;
	const contract = contractNode;

	// Ensure the contract's customer is in the options list (might not be if it's beyond first 50 or inactive)
	const customerOptions = useMemo(() => {
		if (!contract?.customer) return baseCustomerOptions;

		// Check if customer is already in options
		const hasCustomer = baseCustomerOptions.some(
			c => c.customerId === contract.customer!.customerId
		);

		if (hasCustomer) return baseCustomerOptions;

		// Add the contract's customer to the beginning of the list
		const contractCustomer = {
			customerId: contract.customer.customerId,
			firmName:
				contract.customer.firmName ??
				`${contract.customer.firstName ?? ''} ${contract.customer.lastName ?? ''}`.trim(),
			categoryName:
				contract.customer.customerCategory?.description ?? null,
			categoryLimit:
				contract.customer.customerCategory?.simultaneousVehicleLimit ??
				null,
			contactName: null,
			contactEmail: null,
			contactPhone: null
		};

		return [contractCustomer, ...baseCustomerOptions];
	}, [baseCustomerOptions, contract?.customer]);

	// Compute initial customer label from contract data
	const initialCustomerLabel = useMemo(() => {
		if (!contract?.customer) return undefined;

		// For companies, use firmName
		if (contract.customer.isCompany && contract.customer.firmName) {
			return contract.customer.firmName;
		}

		// For individuals, construct name from first/last name
		if (contract.customer.firstName || contract.customer.lastName) {
			return [contract.customer.firstName, contract.customer.lastName]
				.filter(Boolean)
				.join(' ');
		}

		// Fallback to firmName if available
		return contract.customer.firmName ?? undefined;
	}, [contract?.customer]);

	const initialValues = useMemo<ContractFormValues | undefined>(() => {
		if (!contract) {
			return undefined;
		}

		if (contractNode) {
			// Map from GraphQL data
			return {
				customerId: contractNode.customer?.customerId ?? '',
				beginDate: new Date(contractNode.beginDate)
					.toISOString()
					.slice(0, 10),
				endDate: new Date(contractNode.endDate)
					.toISOString()
					.slice(0, 10),
				amount: contractNode.amount?.toString() ?? '',
				isAutoRenewed: contractNode.isAutoRenewed,
				simultaneousVehicleLimit:
					contractNode.simultaneousVehicleLimit?.toString() ?? '',
				reservedParkingSlots:
					contractNode.reservedParkingSlots?.toString() ?? '',
				isActive: contractNode.isActive,
				preferredLanguage:
					(contractNode.preferredLanguage as 'EN' | 'EL' | '') ?? '',
				vehicles:
					contractNode.contractVehicles?.map(v => ({
						contractVehicleId: v.contractVehicleId,
						vehicleId: v.vehicleId,
						plateNumber: v.vehicle?.plateNumber ?? '', // For display only
						rfidTag: v.rfidTag ?? '',
						isVip: v.isVip,
						isActive: true,
						isDeleted: false
					})) ?? []
			};
		}

		return undefined;
	}, [contractNode]);

	const handleSubmit = async (payload: ContractFormSubmitData) => {
		if (!contract?.contractId) {
			return;
		}

		setIsSubmitting(true);
		try {
			if (contractNode) {
				// Build contractVehicles array with proper structure for update
				// Note: Vehicle details (plateNumber, make, model, color) are no longer
				// included in UpdateContractVehicleInput - only contract vehicle relationship data
				const contractVehicles = payload.vehicles.map(vehicle => {
					const contractVehicleId =
						vehicle.contractVehicleId || ulid();

					// If vehicleId exists and looks like a ULID, use it; otherwise generate new one
					let vehicleId = vehicle.vehicleId;
					if (
						!vehicleId?.match(
							/^[0123456789ABCDEFGHJKMNPQRSTVWXYZ]{26}$/i
						)
					) {
						vehicleId = ulid();
					}

					// Build contract vehicle data according to UpdateContractVehicleInput schema
					const contractVehicleData: any = {
						contractVehicleId,
						vehicleId,
						isVip: vehicle.isVip
					};

					// Only add rfidTag if it has a value
					if (vehicle.rfidTag?.trim()) {
						contractVehicleData.rfidTag = vehicle.rfidTag.trim();
					}

					return contractVehicleData;
				});

				// Use real GraphQL mutation
				const { data: mutationData } = await updateContractMutation(
					getMutationOptions('contracts', {
						input: {
							contractId: contract.contractId,
							contractNumber: contract.contractNumber,
							customerId: payload.customerId,
							beginDate: new Date(
								payload.beginDate
							).toISOString(),
							endDate: new Date(payload.endDate).toISOString(),
							amount: payload.amount,
							isAutoRenewed: payload.isAutoRenewed,
							simultaneousVehicleLimit:
								payload.simultaneousVehicleLimit || null,
							reservedParkingSlots:
								payload.reservedParkingSlots || 0,
							isActive: payload.isActive,
							preferredLanguage: payload.preferredLanguage,
							contractVehicles
						}
					})
				);

				if (mutationData?.updateContract?.success) {
					toast.success('Contract updated successfully.');
					go({
						to: '/contracts',
						state: { selectContractId: contract.contractId }
					});
				} else {
					const errorMessage =
						mutationData?.updateContract?.errorDescription ||
						mutationData?.updateContract?.errorCode ||
						'Unknown error occurred';
					toast.error(`Failed to update contract: ${errorMessage}`);
				}
			} else {
				toast.error('Contract data not available for update.');
			}
		} catch (error) {
			toast.error('Failed to update contract. Please try again.');
		} finally {
			setIsSubmitting(false);
		}
	};

	if (shouldShowLoading) {
		return (
			<FormPageLayout>
				<DelayedSkeleton
					isLoading={true}
					delay={300}
					showInitialLoading={false}
				>
					<div>Loading contract...</div>
				</DelayedSkeleton>
			</FormPageLayout>
		);
	}

	if (error && !data) {
		return (
			<FormPageLayout>
				<Alert variant="destructive">
					<AlertTitle>Unable to load contract</AlertTitle>
					<AlertDescription>
						There was an error loading the contract data. Please try
						again.
					</AlertDescription>
				</Alert>
			</FormPageLayout>
		);
	}

	if (!contract || !initialValues) {
		return (
			<FormPageLayout>
				<Alert variant="destructive">
					<AlertTitle>Contract unavailable</AlertTitle>
					<AlertDescription>
						The contract you are trying to edit could not be found.
					</AlertDescription>
				</Alert>
			</FormPageLayout>
		);
	}

	return (
		<FormPageLayout>
			<ContractForm
				mode="edit"
				initialData={initialValues}
				onSubmit={handleSubmit}
				isSubmitting={isSubmitting}
				customerOptions={customerOptions}
				initialCustomerLabel={initialCustomerLabel}
				headerTitle={contract?.customer?.firmName ?? 'Edit contract'}
				contractNumber={contract.contractNumber}
			/>
		</FormPageLayout>
	);
};
